/** One frame of a scene as a picture's address, for every place that shows
 *  a picture of a scene outside the film page (docs/SPEC.md §P28.2 (2),
 *  P28-T10): the templates' picker, a question's candidate scenes, the review
 *  link's scenes, the Ads list, and a stand-in's picture in the cast panel
 *  (one object of a scene framed alone, `target`, P28-T16). Drawn by the player in the browser
 *  (`frames.ts::frameOf`, one offscreen player at a time) at the scene's
 *  opening moment with its captions in, from the manifest `GET …/manifest`
 *  answers; kept for the
 *  page's life by scene, bake and size, so a picture is drawn once however
 *  many places show it. The film's environment of the page (a test's
 *  recorder, the overlay's stand-in) is handed to the offscreen player. */
import {
  createElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useManifest } from "../api/versions";
import { FilmEnvContext } from "./context";
import { type FrameSize, frameOf, frameUrl, openingMoment } from "./frames";
import type { Manifest, ManifestScene } from "./manifest";
import { OverlayFrameContext } from "./useOverlayFrame";

/** The pictures drawn or being drawn, by scene, bake, size and object. */
const drawn = new Map<string, Promise<string>>();

const keyOf = (scene: ManifestScene, size: FrameSize, target: string | null) =>
  `${scene.id}|${(scene.glb_url ?? "").split("?")[0]}|${size.width}x${size.height}|${target ?? ""}`;

/** Wraps an offscreen player in the page's film environment. */
export function useFrameWrap(): (node: ReactNode) => ReactNode {
  const env = useContext(FilmEnvContext);
  const overlay = useContext(OverlayFrameContext);
  return useMemo(
    () => (node: ReactNode) => {
      const inner = overlay
        ? createElement(OverlayFrameContext.Provider, { value: overlay }, node)
        : node;
      return env
        ? createElement(FilmEnvContext.Provider, { value: env }, inner)
        : inner;
    },
    [env, overlay],
  );
}

/** The scene of `manifest` a picture shows: `sceneId`, or the first built. */
export function sceneOf(
  manifest: Manifest | null | undefined,
  sceneId: string | null,
): ManifestScene | null {
  if (!manifest) return null;
  const scene =
    sceneId === null
      ? manifest.scenes.find((s) => s.ready)
      : manifest.scenes.find((s) => s.id === sceneId);
  return scene?.ready ? scene : null;
}

/** The picture of scene `sceneId` of `manifest` (the first built scene when
 *  null) at `size`, or null until it is drawn (and when it cannot be). With
 *  `target`, that object of the scene alone, without the captions. */
export function useFrame(
  manifest: Manifest | null | undefined,
  sceneId: string | null,
  size: FrameSize,
  enabled = true,
  target: string | null = null,
): string | null {
  const wrap = useFrameWrap();
  const scene = sceneOf(manifest, sceneId);
  const key = scene ? keyOf(scene, size, target) : null;
  const [shown, setShown] = useState<{ key: string; url: string } | null>(null);
  useEffect(() => {
    if (!enabled || !manifest || !scene || !key) return;
    let live = true;
    let asked = drawn.get(key);
    if (!asked) {
      asked = frameOf(
        manifest,
        scene.id,
        openingMoment(scene),
        size,
        target === null
          ? // its captions in: an end card is its words, not a bare stage
            { wrap, captions: true }
          : { wrap, target },
      ).then((bitmap) => {
        const url = frameUrl(bitmap);
        bitmap.close?.();
        return url;
      });
      drawn.set(key, asked);
      // a picture that could not be drawn is asked again next time
      asked.catch(() => drawn.delete(key));
    }
    asked.then(
      (url) => {
        if (live) setShown({ key, url });
      },
      () => undefined, // stays grey
    );
    return () => {
      live = false;
    };
    // the manifest is read again with new links: the key says what changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);
  return shown && shown.key === key ? shown.url : null;
}

/** As `useFrame`, from the manifest's address (read when `enabled`). */
export function useManifestFrame(
  manifestUrl: string | null | undefined,
  sceneId: string | null,
  size: FrameSize,
  enabled = true,
): string | null {
  const manifest = useManifest(enabled ? manifestUrl : null);
  return useFrame(manifest.data ?? null, sceneId, size, enabled);
}

/** Forgets every picture (tests). */
export function forgetFrames(): void {
  drawn.clear();
}
